import React, { useState } from 'react';
import { TrainingVideo, videosAPI } from '../../services/api';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Modal, Button, Card, CardBody } from '../ui';

interface VideoEditProps {
  video: TrainingVideo;
  onUpdateSuccess: () => void;
  onClose: () => void;
}

const VideoEdit: React.FC<VideoEditProps> = ({ video, onUpdateSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    title: video.title,
    description: video.description || '',
    duration: video.duration?.toString() || '',
    order: video.order?.toString() || '',
    isRequired: video.isRequired,
    isActive: video.isActive,
    language: (video as any).language || 'ARABIC',
  });
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError('Please enter a video title');
      return;
    }

    setUpdating(true);
    setError('');
    setSuccess('');

    try {
      const updateData: any = {
        title: formData.title,
        description: formData.description || undefined,
        duration: formData.duration ? parseInt(formData.duration) : undefined,
        order: formData.order ? parseInt(formData.order) : undefined,
        isRequired: formData.isRequired,
        isActive: formData.isActive,
        language: formData.language,
      };

      await videosAPI.update(video.id, updateData);
      setSuccess('Video updated successfully!');
      setTimeout(() => {
        onUpdateSuccess();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update video');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Edit Training Video" size="lg">
      {error && (
        <Card variant="bordered" className="border-error-200 bg-error-50 mb-4">
          <CardBody className="p-3 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-error-500 mr-2" />
            <span className="text-sm text-error-600">{error}</span>
          </CardBody>
        </Card>
      )}

      {success && (
        <Card variant="bordered" className="border-success-200 bg-success-50 mb-4">
          <CardBody className="p-3 flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-success-500 mr-2" />
            <span className="text-sm text-success-600">{success}</span>
          </CardBody>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700">Title *</label>
          <input
            type="text"
            name="title"
            required
            value={formData.title}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            placeholder="Enter video title"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700">Description</label>
          <textarea
            name="description"
            rows={3}
            value={formData.description}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            placeholder="Enter video description"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700">Duration (seconds)</label>
            <input
              type="number"
              name="duration"
              min="0"
              value={formData.duration}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
              placeholder="e.g., 300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">Order</label>
            <input
              type="number"
              name="order"
              min="0"
              value={formData.order}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
              placeholder="e.g., 1"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700">Language *</label>
          <select
            name="language"
            value={formData.language}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
          >
            <option value="ARABIC">Arabic</option>
            <option value="ENGLISH">English</option>
          </select>
        </div>

        <div className="space-y-2">
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isRequired"
              checked={formData.isRequired}
              onChange={handleInputChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
            />
            <label className="ml-2 block text-sm text-neutral-900">
              Required for training completion
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
            />
            <label className="ml-2 block text-sm text-neutral-900">
              Active (visible to users)
            </label>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" isLoading={updating}>
            Update Video
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default VideoEdit;

